import { Component, computed, inject, input, output } from '@angular/core';
import { MODULE_STORE, ModuleAction } from '@marineform/manage-data-access';
import { TranslocoPipe } from '@ngneat/transloco';
import {
  SscButtonComponent,
  SscButtonHierarchy,
  SscButtonSize,
  SscIconComponent,
  SscMenuItemComponent,
  SscMenuItemHierarchy,
  SscEmptyPlaceholderComponent
} from '@marineform/ssc-ui';
import { IonicModule, IonNav, ModalController } from '@ionic/angular';

@Component({
  selector: 'app-module-actions',
  imports: [SscEmptyPlaceholderComponent, SscMenuItemComponent, TranslocoPipe],
  template: `
    @if (visibleActions().length) {
      <div class="elevate-card flex w-full flex-col">
        @for (action of visibleActions(); track action.label) {
          <button
            type="button"
            ssc-menu-item
            tall
            [svg]="action.icon"
            [hierarchy]="action.hierarchy ?? menuItemHierarchy.normal"
            (click)="handleActionClick(action)">
            {{ action.label | transloco }}
          </button>
        }
      </div>
    } @else {
      <ssc-empty-placeholder
        title="actions|empty|title"
        message="actions|empty|message"
        icon="cursor-click" />
    }
  `,
  host: {
    class: 'block w-full',
  },
})
export class ModuleActionsComponent {
  record = inject(MODULE_STORE).record;
  #modal = inject(ModalController);
  nav = input<IonNav>();
  actions = input<ModuleAction[]>([]);
  menuItemHierarchy = SscMenuItemHierarchy;
  /** When true, actions are filtered with a null record (list context). */
  allowWithoutRecord = input(false);
  onActionClick = output<ModuleAction>();

  visibleActions = computed(() => {
    const record = this.record();
    if (record == null && !this.allowWithoutRecord()) {
      return [];
    }
    return this.actions().filter((action) => {
      if (!action.interactivity(record)) {
        return false;
      }
      const children = action.children;
      if (children?.length) {
        return children.some((child) => child.interactivity(record));
      }
      return true;
    });
  });

  handleActionClick(action: ModuleAction): void {
    const record = this.record();
    const children = action.children?.filter((child) => {
      if (record == null && !this.allowWithoutRecord()) {
        return false;
      }
      return child.interactivity(record);
    });
    if (children?.length) {
      this.nav()?.push(ModuleActionsNestedAreaComponent, {
        nav: this.nav(),
        title: action.label,
        actions: children,
      });
      return;
    }
    this.#modal.dismiss(action).then(() => {
      this.onActionClick.emit(action);
    });
  }
}

@Component({
  selector: 'app-module-actions-nested',
  imports: [
    IonicModule,
    SscButtonComponent,
    SscIconComponent,
    TranslocoPipe,
    ModuleActionsComponent,
  ],
  template: `
    <ion-header class="ion-no-border">
      <ion-toolbar>
        <ion-buttons slot="start">
          <button
            type="button"
            ssc-button
            [size]="buttonSize.small"
            [hierarchy]="buttonHierarchy.inverted"
            (click)="back()">
            <ssc-icon small svg="arrow-bend-up-left" />
          </button>
        </ion-buttons>
        <ion-title size="large">{{ title() | transloco }}</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding mg-padded" [fullscreen]="true">
      <app-module-actions [nav]="nav()" [actions]="actions()" />
    </ion-content>
  `,
})
class ModuleActionsNestedAreaComponent {
  nav = input.required<IonNav>();
  title = input.required<string>();
  actions = input<ModuleAction[]>([]);
  buttonHierarchy = SscButtonHierarchy;
  buttonSize = SscButtonSize;
  back(): void {
    void this.nav().pop();
  }
}
